import { useState, useMemo, useEffect } from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider,
  Slider,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useCorpusData } from '../../context/CorpusDataContext'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin,
  ChartDataLabels
)

// ASHRAE benchmark bands - efficiency thresholds by equipment age
const ashraeBenchmarks = {
  bands: [
    { name: 'ASHRAE 2nd Year', min: 90, max: 100, color: 'rgba(76, 175, 80, 0.25)', borderColor: '#4CAF50' },
    { name: 'ASHRAE 5th Year', min: 85, max: 90, color: 'rgba(139, 195, 74, 0.25)', borderColor: '#8BC34A' },
    { name: 'ASHRAE 10th Year', min: 75, max: 85, color: 'rgba(255, 235, 59, 0.25)', borderColor: '#FFEB3B' },
    { name: 'ASHRAE 15th Year', min: 0, max: 75, color: 'rgba(255, 152, 0, 0.25)', borderColor: '#FF9800' }
  ],
  degradationRates: {
    year1: 7,    // 7% loss first year
    year2: 5,    // 5% loss second year
    yearAfter: 2 // 2% per year thereafter
  }
}


// Aggregate real case study data by equipment age
function aggregateByAge(data, ageRange) {
  const byAge = {}

  data.forEach(cs => {
    const age = cs.equipment_age
    if (!age || age < ageRange[0] || age > ageRange[1]) return

    if (!byAge[age]) {
      byAge[age] = { baseEffs: [], postEffs: [], count: 0 }
    }

    // Use efficiency_pct directly if available, otherwise calculate from kw_per_ton
    if (cs.kw_per_ton_before && cs.kw_per_ton_after) {
      // Convert kW/Ton to efficiency % (lower kW/Ton = better efficiency)
      // Using inverse relationship and scaling for display
      const baselineKwTon = 0.8 // Reference point for 100% efficiency
      const baseEff = Math.min(100, (baselineKwTon / cs.kw_per_ton_before) * 100)
      const postEff = Math.min(100, (baselineKwTon / cs.kw_per_ton_after) * 100)

      byAge[age].baseEffs.push(baseEff)
      byAge[age].postEffs.push(postEff)
      byAge[age].count++
    }
  })

  return Object.entries(byAge)
    .map(([age, ageData]) => ({
      age: parseInt(age),
      baseEff: ageData.baseEffs.length > 0
        ? ageData.baseEffs.reduce((a, b) => a + b, 0) / ageData.baseEffs.length
        : null,
      postEff: ageData.postEffs.length > 0
        ? ageData.postEffs.reduce((a, b) => a + b, 0) / ageData.postEffs.length
        : null,
      records: ageData.count
    }))
    .filter(d => d.baseEff !== null && d.postEff !== null)
    .sort((a, b) => a.age - b.age)
}

// Get individual study data points (one per study, not aggregated)
function getIndividualStudyData(data) {
  const baselineKwTon = 0.8 // Reference point for 100% efficiency

  return data
    .filter(cs => cs.kw_per_ton_before && cs.kw_per_ton_after)
    .map(cs => {
      const baseEff = Math.min(100, (baselineKwTon / cs.kw_per_ton_before) * 100)
      const postEff = Math.min(100, (baselineKwTon / cs.kw_per_ton_after) * 100)
      // Use cs.id (from transformToCaseStudy) not cs.study_id
      const studyId = cs.id || 'Unknown'
      return {
        study_id: studyId,
        age: cs.equipment_age,
        label: `${studyId} (${cs.equipment_age}yr)`,
        baseEff,
        postEff,
        records: 1
      }
    })
    .sort((a, b) => a.age - b.age)
}

function ASHRAEBenchmarkChart({ showULLabTested = false }) {
  // Get case studies from corpus data context
  const { caseStudies, loading } = useCorpusData()

  // Derive equipment types from loaded data
  const equipmentTypes = useMemo(() => {
    if (!caseStudies) return []
    const types = new Set()
    caseStudies.forEach(cs => {
      if (cs.equipment_type) types.add(cs.equipment_type)
    })
    return Array.from(types).sort().map(type => ({
      id: type,
      label: type
    }))
  }, [caseStudies])

  // Derive manufacturers from loaded data
  const manufacturers = useMemo(() => {
    if (!caseStudies) return []
    const mfrs = new Set()
    caseStudies.forEach(cs => {
      if (cs.manufacturer) mfrs.add(cs.manufacturer)
    })
    return Array.from(mfrs).sort()
  }, [caseStudies])

  // Filter states - initialize empty, will be populated by useEffect
  const [ageRange, setAgeRange] = useState([2, 28])
  const [capacityRange, setCapacityRange] = useState([1, 2268])
  const [selectedEquipment, setSelectedEquipment] = useState(new Set())
  const [selectedManufacturers, setSelectedManufacturers] = useState(new Set())
  const [manufacturerSearch, setManufacturerSearch] = useState('')
  const [viewMode, setViewMode] = useState('all') // 'all' or 'ul_lab'

  // Initialize selections when data loads
  useEffect(() => {
    if (equipmentTypes.length > 0 && selectedEquipment.size === 0) {
      setSelectedEquipment(new Set(equipmentTypes.map(e => e.id)))
    }
  }, [equipmentTypes])

  useEffect(() => {
    if (manufacturers.length > 0 && selectedManufacturers.size === 0) {
      setSelectedManufacturers(new Set(manufacturers))
    }
  }, [manufacturers])

  // Generate chart data from real case studies with filters applied
  const chartData = useMemo(() => {
    if (!caseStudies) return { labels: [], datasets: [] }

    const isUL = viewMode === 'ul_lab'

    // Filter case studies based on view mode and selected filters
    const filteredData = caseStudies.filter(cs => {
      // UL Lab mode: show ALL lab_report studies, ignore equipment/manufacturer/capacity filters
      if (isUL) {
        return cs.category === 'lab_report'
      }

      // All Equipment mode: apply all filters
      // Equipment type filter
      if (!selectedEquipment.has(cs.equipment_type)) return false

      // Manufacturer filter
      if (cs.manufacturer && !selectedManufacturers.has(cs.manufacturer)) return false

      // Capacity filter (only if capacity is defined)
      if (cs.capacity_tons != null && (cs.capacity_tons < capacityRange[0] || cs.capacity_tons > capacityRange[1])) return false

      return true
    })

    // For UL Lab view: show individual studies; for All view: aggregate by age
    const data = isUL
      ? getIndividualStudyData(filteredData)
      : aggregateByAge(filteredData, ageRange)

    // Labels: study IDs for UL view, ages for All view
    const labels = isUL
      ? data.map(d => d.label)
      : data.map(d => d.age)

    return {
      labels,
      datasets: [
        {
          label: isUL ? 'Base Eff' : 'Avg. Base Eff',
          data: data.map(d => d.baseEff),
          borderColor: '#2196F3',
          backgroundColor: '#2196F3',
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 6,
          pointHoverRadius: 8,
          yAxisID: 'y',
          order: 1
        },
        {
          label: isUL ? 'Post Eff' : 'Avg. Post Unit Eff',
          data: data.map(d => d.postEff),
          borderColor: '#FF9800',
          backgroundColor: '#FF9800',
          borderWidth: 2,
          borderDash: [5, 5],
          tension: 0.3,
          pointRadius: 6,
          pointHoverRadius: 8,
          yAxisID: 'y1',
          order: 0
        }
      ],
      rawData: data,
      totalFiltered: filteredData.length
    }
  }, [caseStudies, ageRange, viewMode, selectedEquipment, selectedManufacturers, capacityRange])

  // Build annotation boxes for ASHRAE bands
  const annotations = useMemo(() => {
    const result = {}

    ashraeBenchmarks.bands.forEach((band, index) => {
      result[`band${index}`] = {
        type: 'box',
        yMin: band.min,
        yMax: band.max,
        backgroundColor: band.color,
        borderWidth: 0,
        label: {
          display: index < 4,
          content: band.name,
          position: { x: 'start', y: 'center' },
          font: { size: 10 },
          color: '#666'
        }
      }
    })

    // Add horizontal reference lines for each benchmark
    ashraeBenchmarks.bands.forEach((band, index) => {
      if (band.min > 0) {
        result[`line${index}`] = {
          type: 'line',
          yMin: band.min,
          yMax: band.min,
          borderColor: band.borderColor,
          borderWidth: 1,
          borderDash: [3, 3]
        }
      }
    })

    return result
  }, [])

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 20
        }
      },
      title: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        callbacks: {
          title: (context) => {
            const isUL = viewMode === 'ul_lab'
            const label = context[0].label
            return isUL ? label : `Unit Age: ${label} years`
          },
          label: (context) => {
            const value = context.raw
            return `${context.dataset.label}: ${value.toFixed(2)}%`
          },
          afterBody: (context) => {
            const idx = context[0].dataIndex
            const rawData = chartData.rawData[idx]
            const improvement = ((rawData.postEff - rawData.baseEff) / rawData.baseEff * 100).toFixed(1)
            const isUL = viewMode === 'ul_lab'
            return [
              '',
              `Improvement: +${improvement}%`,
              isUL ? '' : `Number of Records: ${rawData.records}`
            ].filter(Boolean)
          }
        }
      },
      annotation: {
        annotations
      },
      datalabels: {
        display: (context) => {
          // For UL view show all labels, for All view show every other to reduce clutter
          return viewMode === 'ul_lab' || context.dataIndex % 2 === 0
        },
        anchor: (context) => {
          return context.dataset.label.includes('Base') ? 'start' : 'end'
        },
        align: (context) => {
          return context.dataset.label.includes('Base') ? 'bottom' : 'top'
        },
        offset: 4,
        font: { size: 9 },
        color: (context) => {
          return context.dataset.label.includes('Base') ? '#1565C0' : '#E65100'
        },
        formatter: (value) => `${value.toFixed(1)}%`
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: viewMode === 'ul_lab' ? 'Study' : 'Unit Age (Years)',
          font: { size: 12 }
        },
        grid: {
          display: false
        }
      },
      y: {
        type: 'linear',
        position: 'left',
        min: 40,
        max: 110,
        title: {
          display: true,
          text: viewMode === 'ul_lab' ? 'Base Eff (%)' : 'Avg. Base Eff (%)',
          color: '#2196F3',
          font: { size: 11 }
        },
        ticks: {
          callback: (value) => `${value.toFixed(0)}%`,
          color: '#2196F3'
        },
        grid: {
          color: 'rgba(0,0,0,0.05)'
        }
      },
      y1: {
        type: 'linear',
        position: 'right',
        min: 40,
        max: 110,
        title: {
          display: true,
          text: viewMode === 'ul_lab' ? 'Post Eff (%)' : 'Avg. Post Unit Eff (%)',
          color: '#FF9800',
          font: { size: 11 }
        },
        ticks: {
          callback: (value) => `${value.toFixed(0)}%`,
          color: '#FF9800'
        },
        grid: {
          drawOnChartArea: false
        }
      }
    }
  }

  // Toggle functions
  const toggleEquipment = (id) => {
    setSelectedEquipment(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) newSet.delete(id)
      else newSet.add(id)
      return newSet
    })
  }

  const toggleAllEquipment = () => {
    if (selectedEquipment.size === equipmentTypes.length) {
      setSelectedEquipment(new Set())
    } else {
      setSelectedEquipment(new Set(equipmentTypes.map(e => e.id)))
    }
  }

  const filteredManufacturers = manufacturers.filter(m =>
    m.toLowerCase().includes(manufacturerSearch.toLowerCase())
  )

  const toggleManufacturer = (mfr) => {
    setSelectedManufacturers(prev => {
      const newSet = new Set(prev)
      if (newSet.has(mfr)) newSet.delete(mfr)
      else newSet.add(mfr)
      return newSet
    })
  }

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* View Toggle */}
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, value) => value && setViewMode(value)}
          size="small"
        >
          <ToggleButton value="all">All Equipment</ToggleButton>
          <ToggleButton value="ul_lab">UL Lab Tested</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        {/* Main Chart Area */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {viewMode === 'ul_lab' ? 'UL Lab Tested' : 'Age Eff'}
            </Typography>
            <Chip
              label={`${chartData.totalFiltered} studies`}
              size="small"
              color={chartData.totalFiltered > 0 ? 'primary' : 'default'}
              variant="outlined"
            />
          </Box>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip
              label="ASHRAE 2nd Year"
              size="small"
              sx={{ bgcolor: 'rgba(76, 175, 80, 0.3)', color: '#2E7D32' }}
            />
            <Chip
              label="ASHRAE 5th Year"
              size="small"
              sx={{ bgcolor: 'rgba(139, 195, 74, 0.3)', color: '#558B2F' }}
            />
            <Chip
              label="ASHRAE 10th Year"
              size="small"
              sx={{ bgcolor: 'rgba(255, 235, 59, 0.3)', color: '#F57F17' }}
            />
            <Chip
              label="ASHRAE 15th Year"
              size="small"
              sx={{ bgcolor: 'rgba(255, 152, 0, 0.3)', color: '#E65100' }}
            />
          </Stack>

          <Box sx={{ height: 350, minHeight: 300 }}>
            {chartData.labels.length > 0 ? (
              <Line data={chartData} options={options} />
            ) : (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
                bgcolor: 'action.hover',
                borderRadius: 1
              }}>
                <Typography>No data matches the current filters. Adjust filters to see results.</Typography>
              </Box>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Background bands show ASHRAE efficiency benchmarks. Blue line shows expected degradation without treatment.
            Orange dashed line shows sustained efficiency with PermaFrost NMR treatment.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
            Note: ASHRAE degradation estimates represent baseline laboratory conditions. Field studies from the 116 PE-certified
            corpus show actual degradation often exceeds these baselines, making documented treatment benefits significant.
          </Typography>
        </Box>

        {/* Filter Panel - Right Side */}
        <Paper
          elevation={0}
          sx={{
            width: 130,
            flexShrink: 0,
            p: 1.5,
            bgcolor: (theme) => theme.palette.mode === 'light'
              ? 'rgba(255,255,255,0.9)'
              : 'rgba(0,0,0,0.3)',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            alignSelf: 'flex-start',
            backdropFilter: 'blur(8px)'
          }}
        >
          {/* Equipment Type Filter */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Equipment Type
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={selectedEquipment.size === equipmentTypes.length}
                  indeterminate={selectedEquipment.size > 0 && selectedEquipment.size < equipmentTypes.length}
                  onChange={toggleAllEquipment}
                />
              }
              label={<Typography variant="body2">(All)</Typography>}
            />
            {equipmentTypes.slice(0, 6).map(eq => (
              <FormControlLabel
                key={eq.id}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedEquipment.has(eq.id)}
                    onChange={() => toggleEquipment(eq.id)}
                  />
                }
                label={<Typography variant="body2">{eq.label}</Typography>}
              />
            ))}
          </FormGroup>

          <Divider sx={{ my: 2 }} />

          {/* Manufacturer Filter with Search */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Manufacturer
          </Typography>
          <TextField
            size="small"
            placeholder="Search..."
            value={manufacturerSearch}
            onChange={(e) => setManufacturerSearch(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'grey.500' }} />
                </InputAdornment>
              )
            }}
          />
          <FormGroup>
            {filteredManufacturers.slice(0, 6).map(mfr => (
              <FormControlLabel
                key={mfr}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedManufacturers.has(mfr)}
                    onChange={() => toggleManufacturer(mfr)}
                  />
                }
                label={<Typography variant="body2" noWrap>{mfr}</Typography>}
              />
            ))}
          </FormGroup>

          <Divider sx={{ my: 2 }} />

          {/* Age Range Slider */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Avg. Unit Age
          </Typography>
          <Box sx={{ px: 1 }}>
            <Slider
              value={ageRange}
              onChange={(e, newValue) => setAgeRange(newValue)}
              min={1}
              max={30}
              valueLabelDisplay="auto"
              size="small"
              disabled={viewMode === 'ul_lab'}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption">{ageRange[0]}</Typography>
              <Typography variant="caption">{ageRange[1]}</Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Capacity Range Slider */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Avg. Rated Capacity
          </Typography>
          <Box sx={{ px: 1 }}>
            <Slider
              value={capacityRange}
              onChange={(e, newValue) => setCapacityRange(newValue)}
              min={1}
              max={2268}
              valueLabelDisplay="auto"
              size="small"
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption">{capacityRange[0]}</Typography>
              <Typography variant="caption">{capacityRange[1]}</Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Legend */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Measure Names
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ width: 20, height: 3, bgcolor: '#2196F3', mr: 1 }} />
            <Typography variant="body2">Avg. Base Eff</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              width: 20,
              height: 3,
              background: 'repeating-linear-gradient(90deg, #FF9800, #FF9800 3px, transparent 3px, transparent 6px)',
              mr: 1
            }} />
            <Typography variant="body2">Avg. Post Unit Eff</Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  )
}

export default ASHRAEBenchmarkChart
